import type { MouseEvent } from "react";
import { useState } from "react";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Grid,
    Chip,
    TextField,
    InputAdornment,
    Button,
    useTheme,
    Alert,
    Skeleton,
} from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { useGetTopics, Topic } from "../../../../api/client-api/social/useTopics";
import { useToggleTopicFollow, useGetUserFollowingTopics } from "../../../../api/client-api/social/useTopicFollow";
import { useTranslation } from "react-i18next";

interface AllTopicsProps {
    onTopicClick?: (topicId: number, topicName: string) => void;
    onClose?: () => void;
}

export const AllTopics = ({ onTopicClick, onClose }: AllTopicsProps) => {
    const theme = useTheme();
    const [searchTerm, setSearchTerm] = useState("");
    const { t } = useTranslation();
    const {
        data: topics,
        isLoading,
        isError,
    } = useGetTopics({
        name: searchTerm || undefined,
        active: true,
    });

    const { data: userFollowingTopics } = useGetUserFollowingTopics();
    const toggleFollow = useToggleTopicFollow();

    const filteredTopics =
        topics?.filter(
            (topic) =>
                topic.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                topic.description.toLowerCase().includes(searchTerm.toLowerCase()),
        ) || [];

    const isTopicFollowed = (topicId: number) => {
        return userFollowingTopics?.topics.some((topic) => topic.id === topicId) || false;
    };

    const handleTopicClick = (topic: Topic) => {
        if (onTopicClick) {
            onTopicClick(topic.id, topic.name);
        }
        if (onClose) {
            onClose();
        }
    };

    const handleFollowClick = (e: MouseEvent, topicId: number) => {
        e.stopPropagation(); // Prevent topic click
        toggleFollow.mutate(topicId);
    };

    if (isLoading) {
        return (
            <Card>
                <CardHeader
                    title={t("social.topics.all", "All Topics")}
                    avatar={<FoodsIcon icon="settings" size={2} />}
                />
                <CardContent>
                    <Box mb={3}>
                        <Skeleton variant="rectangular" height={40} />
                    </Box>
                    <Grid container spacing={2}>
                        {Array.from({ length: 8 }).map((_, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
        );
    }

    if (isError) {
        return (
            <Card>
                <CardHeader
                    title={t("social.topics.all", "All Topics")}
                    avatar={<FoodsIcon icon="settings" size={2} />}
                />
                <CardContent>
                    <Alert severity="error">{t("social.topics.error.load", "Failed to load topics")}</Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader
                title={t("social.topics.all", "All Topics")}
                avatar={<FoodsIcon icon="evolution" size={2} />}
                action={
                    onClose && (
                        <Button variant="outlined" size="small" onClick={onClose}>
                            {t("social.topics.close", "Close")}
                        </Button>
                    )
                }
            />
            <CardContent>
                {/* Search */}
                <Box mb={3}>
                    <TextField
                        fullWidth
                        placeholder={t("social.topics.search", "Search topics...")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">🔍</InputAdornment>,
                        }}
                        sx={{ mb: 2 }}
                    />
                </Box>

                {/* Topics Grid */}
                {!filteredTopics.length ?
                    <Box textAlign="center" py={4}>
                        <Typography variant="body2" color="text.secondary">
                            {searchTerm ?
                                t("social.topics.error.no_topics_found", "No topics found matching your search.")
                            :   t("social.topics.error.no_topics_available", "No topics available yet.")}
                        </Typography>
                    </Box>
                :   <Grid container spacing={2}>
                        {filteredTopics.map((topic) => (
                            <Grid item xs={12} sm={6} md={4} key={topic.id}>
                                <Card
                                    sx={{
                                        cursor: "pointer",
                                        transition: "all 0.2s ease-in-out",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: theme.shadows[4],
                                            backgroundColor: topic.color + "05",
                                        },
                                    }}
                                    onClick={() => handleTopicClick(topic)}
                                >
                                    <CardContent>
                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: "50%",
                                                    backgroundColor: topic.color,
                                                }}
                                            />
                                            <Typography variant="h6" fontWeight={600}>
                                                #{topic.name}
                                            </Typography>
                                        </Box>

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            mb={2}
                                            sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {topic.description}
                                        </Typography>

                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Box display="flex" gap={1}>
                                                <Chip
                                                    label={`${topic.postCount} ${t("social.topics.posts", "posts")}`}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: topic.color + "20",
                                                        color: topic.color,
                                                        fontSize: "0.75rem",
                                                    }}
                                                />
                                                <Chip
                                                    label={`${topic.followerCount} ${t("social.topics.followers", "followers")}`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        borderColor: topic.color + "50",
                                                        color: topic.color,
                                                        fontSize: "0.75rem",
                                                    }}
                                                />
                                            </Box>

                                            <Button
                                                size="small"
                                                variant={isTopicFollowed(topic.id) ? "contained" : "outlined"}
                                                onClick={(e) => handleFollowClick(e, topic.id)}
                                                disabled={toggleFollow.isLoading}
                                                sx={{
                                                    minWidth: 80,
                                                    fontSize: "0.75rem",
                                                    backgroundColor:
                                                        isTopicFollowed(topic.id) ? topic.color : "transparent",
                                                    borderColor: topic.color,
                                                    color: isTopicFollowed(topic.id) ? "white" : topic.color,
                                                    "&:hover": {
                                                        backgroundColor:
                                                            isTopicFollowed(topic.id) ?
                                                                topic.color + "CC"
                                                            :   topic.color + "10",
                                                    },
                                                }}
                                            >
                                                {isTopicFollowed(topic.id) ?
                                                    t("social.topics.following", "Following")
                                                :   t("social.topics.follow", "Follow")}
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                }

                {/* Stats */}
                <Box mt={3} pt={2} borderTop={`1px solid ${theme.palette.divider}`}>
                    <Typography variant="caption" color="text.secondary">
                        {filteredTopics.length} {t("social.topics.topic", "topic")}
                        {filteredTopics.length !== 1 ? t("social.topics.topics", "topics") : ""}
                        {searchTerm && ` ${t("social.topics.matching", "matching")} "${searchTerm}"`}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};
